import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import LiveCanvasBoard from '../../components/LiveCanvasBoard/LiveCanvasBoard';
import { selectAdminToken } from '../../admin/sessionSlice';
import { computePuzzleGrid } from '../../utils/puzzleGrid';
import useBoardAspectRatio from '../../utils/useBoardAspectRatio';
import { usableMasterUrls, aspectFits, revealBoardState } from '../../utils/boardPieces';
import { getPuzzleThemeConfig } from '../../utils/puzzleTheme';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  buildFirebaseSessionId,
  subscribeSessionUsers,
} from '../../utils/canvasFirebase';
import {
  groupUsersByTile,
  pickRevealContributor,
  mergeOwnGame,
  buildSlots,
  slotImage,
  slotRectInCell,
} from '../../utils/tileContributors';
import { fetchAssetAsObjectUrl } from '../../utils/themeAsset';
import { fetchGameStages } from '../../functions/stageReport';
import './canvas-reveal.css';

// The download is EXPORT_WIDTH wide; its height follows the artwork's real shape
// (admin images can be any size), so nothing is stretched or cropped.
const EXPORT_WIDTH = 1920;

function loadImageForExport(src, token) {
  return new Promise((resolve, reject) => {
    fetchAssetAsObjectUrl(src, token).then((resolvedSrc) => {
      const isBlobUrl = resolvedSrc.startsWith('blob:');
      const img = new Image();
      // Only needed when the proxy fetch failed and we fell back to the raw S3
      // URL — a same-origin blob: URL doesn't need (and shouldn't set) this.
      if (!isBlobUrl) img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (isBlobUrl) URL.revokeObjectURL(resolvedSrc);
        resolve(img);
      };
      img.onerror = () => {
        if (isBlobUrl) URL.revokeObjectURL(resolvedSrc);
        reject(new Error(`Failed to load image for export: ${src}`));
      };
      img.src = resolvedSrc;
    });
  });
}

/**
 * Draw `img` into the (dx, dy, dw, dh) rect cropped-to-fill (CSS object-fit:
 * cover), matching how LiveCanvasTile displays each tile on screen. Plain
 * drawImage(img, dx, dy, dw, dh) instead would stretch the whole source into
 * the rect, squashing/distorting any tile whose native aspect ratio doesn't
 * match the cell — which is what made the downloaded artwork look "shrunk".
 */
function drawImageCover(ctx, img, dx, dy, dw, dh) {
  const srcRatio = img.naturalWidth / img.naturalHeight;
  const dstRatio = dw / dh;
  let sx = 0;
  let sy = 0;
  let sw = img.naturalWidth;
  let sh = img.naturalHeight;
  if (srcRatio > dstRatio) {
    sw = img.naturalHeight * dstRatio;
    sx = (img.naturalWidth - sw) / 2;
  } else {
    sh = img.naturalWidth / dstRatio;
    sy = (img.naturalHeight - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

/** Draws `img` whole inside the rect (object-fit: contain), centred. */
function drawImageContain(ctx, img, dx, dy, dw, dh) {
  const scale = Math.min(dw / img.naturalWidth, dh / img.naturalHeight);
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  ctx.drawImage(img, dx + (dw - w) / 2, dy + (dh - h) / 2, w, h);
}

/**
 * Like drawImageCover, but only for a fractional region ({x, y, w, h} in 0..1) of
 * the source — used for a player's slice of a WHOLE-piece image (master / outline
 * / auto-fill).
 */
function drawImageRegionCover(ctx, img, region, dx, dy, dw, dh) {
  const rx = region.x * img.naturalWidth;
  const ry = region.y * img.naturalHeight;
  const rw = region.w * img.naturalWidth;
  const rh = region.h * img.naturalHeight;
  const srcRatio = rw / rh;
  const dstRatio = dw / dh;
  let sx = rx;
  let sy = ry;
  let sw = rw;
  let sh = rh;
  if (srcRatio > dstRatio) {
    sw = rh * dstRatio;
    sx = rx + (rw - sw) / 2;
  } else {
    sh = rw / dstRatio;
    sy = ry + (rh - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

// Position-preserving, same as parseImgArray in utils/puzzleTheme.js — a
// missing master piece has to stay `null` at its own index, not be dropped,
// since its index IS its tile_id and outlineUrls/masterUrls are read in
// lockstep by position (see the outlineUrls.map(...) below). Dropping an
// empty entry here used to shift every later tile's master image down by
// one slot relative to its real outline/tile_id — e.g. a theme missing a
// master piece for tile 3 would render tile 4's master image on tile 3's
// cell, tile 5's on tile 4's, and so on, which is exactly the kind of
// "wrong image stitched into the wrong cell" look a misaligned reveal has.
function parseSliceArray(raw) {
  const toUrls = (arr) => arr.map((item) => (item ? s3PublicUrl(item) || null : null));
  if (Array.isArray(raw)) {
    return toUrls(raw);
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return toUrls(parsed);
      }
    } catch {
      /* ignore */
    }
  }
  return [];
}

export default function CanvasReveal() {
  const themeData = useSelector((state) => state.theme.data);
  const themeStatus = useSelector((state) => state.theme.status);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);

  const [usersMap, setUsersMap] = useState({});
  const [downloading, setDownloading] = useState(false);

  const outlineUrls = useMemo(() => {
    const config = getPuzzleThemeConfig(themeData);
    return config.slices ?? [];
  }, [themeData]);

  const masterUrls = useMemo(
    () => usableMasterUrls(outlineUrls, parseSliceArray(themeData?.master_img_array)),
    [outlineUrls, themeData]
  );

  const { macroRows, macroCols } = useMemo(() => {
    const count =
      outlineUrls.length ||
      Math.floor(Number(themeData?.total_puzzles ?? themeData?.puzzle_parts)) ||
      9;
    const savedRows = Math.floor(Number(themeData?.row));
    const savedCols = Math.floor(Number(themeData?.col));
    if (
      Number.isFinite(savedRows) &&
      Number.isFinite(savedCols) &&
      savedRows * savedCols === count
    ) {
      return { macroRows: savedRows, macroCols: savedCols };
    }
    const grid = computePuzzleGrid(count);
    return { macroRows: grid.rows, macroCols: grid.cols };
  }, [outlineUrls.length, themeData?.row, themeData?.col, themeData?.total_puzzles, themeData?.puzzle_parts]);

  const boardAspectRatio = useBoardAspectRatio(outlineUrls[0] || null, macroRows, macroCols);

  const boardState = revealBoardState({ pieceCount: outlineUrls.length, themeData, themeStatus });

  const titleColor = themeData?.landing_page_title_color ?? themeData?.text_color ?? null;
  const downloadEnabled =
    themeData?.download_enabled === true || themeData?.download_enabled === 'true';

  const pageStyle = useMemo(
    () => ({
      '--puzzle-title-color': titleColor || undefined,
      '--board-aspect': String(boardAspectRatio),
    }),
    [titleColor, boardAspectRatio]
  );

  const organizationId = user?.organizationId;
  const sessionId = user?.sessionId;
  const firebaseSessionId = buildFirebaseSessionId(organizationId, sessionId);

  // LiveCanvasBoard subscribes internally and doesn't expose its state, so
  // this page keeps its own lightweight subscription for the download composite
  // (needs each tile's resolved image key).
  useEffect(() => {
    if (!firebaseSessionId) return undefined;
    return subscribeSessionUsers(firebaseSessionId, setUsersMap);
  }, [firebaseSessionId]);

  // The viewer's own finished tile as the backend recorded it. Firebase only learns about a finished drawing
  // from a fire-and-forget write; if that write was lost the viewer used to see the coloured piece instead of
  // their own picture. Asked once; a failure just leaves the Firebase data as it is.
  const [ownGame, setOwnGame] = useState(null);
  useEffect(() => {
    const viewerId = user?.userId;
    const token = adminToken || (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);
    if (!viewerId || !token || !sessionId || !organizationId) return undefined;
    let cancelled = false;
    fetchGameStages({ token, userId: viewerId, organizationId, sessionId })
      .then((data) => {
        if (!cancelled && data?.game) setOwnGame(data.game);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.userId, adminToken, sessionId, organizationId]);
  const ownName = String(user?.name || user?.email || '');

  const tileGroups = useMemo(
    () => groupUsersByTile(mergeOwnGame(usersMap, user?.userId, ownGame, { name: ownName })),
    [usersMap, user?.userId, ownGame, ownName]
  );

  const revealTitle = String(themeData?.reveal_title ?? '').trim();

  const handleDownload = useCallback(
    async (format = 'png') => {
      if (!outlineUrls.length || downloading) return;
      setDownloading(true);
      try {
        const token =
          adminToken || (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

        const canvas = document.createElement('canvas');
        const exportHeight = Math.round(EXPORT_WIDTH / boardAspectRatio);
        canvas.width = EXPORT_WIDTH;
        canvas.height = exportHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to create export canvas');

        const cellW = EXPORT_WIDTH / macroCols;
        const cellH = exportHeight / macroRows;

        for (let index = 0; index < outlineUrls.length; index += 1) {
          const tileId = index + 1;
          const row = Math.floor(index / macroCols);
          const col = index % macroCols;
          const fallbackSrc = masterUrls[index] || outlineUrls[index];

          // A DIVIDED piece (more players than pieces, Live mode): each player's
          // slice is drawn into its exact rectangle of the cell, so the exported
          // picture is the fully assembled artwork. Unfinished slices fall back to
          // the matching region of the reference image.
          const sliced = buildSlots(tileGroups[tileId] || []);
          if (sliced) {
            for (const slot of sliced.slots) {
              const image = slotImage(slot.rep, {
                previews: {},
                masterSrc: masterUrls[index],
                outlineSrc: outlineUrls[index],
                useMasterFallback: true,
                resolveKey: s3PublicUrl,
              });
              if (!image.src) continue;
              const dest = slotRectInCell(slot.rect, col * cellW, row * cellH, cellW, cellH);
              // eslint-disable-next-line no-await-in-loop
              const slotImg = await loadImageForExport(image.src, token);
              if (image.crop) {
                drawImageRegionCover(ctx, slotImg, slot.rect, dest.x, dest.y, dest.w, dest.h);
              } else if (
                fallbackSrc &&
                !aspectFits(slotImg.naturalWidth / slotImg.naturalHeight, dest.w / dest.h)
              ) {
                // Saved before the artwork was re-pieced: wrong shape for this cell.
                // eslint-disable-next-line no-await-in-loop
                const ref = await loadImageForExport(fallbackSrc, token);
                drawImageRegionCover(ctx, ref, slot.rect, dest.x, dest.y, dest.w, dest.h);
              } else {
                drawImageCover(ctx, slotImg, dest.x, dest.y, dest.w, dest.h);
              }
            }
            continue;
          }

          // Undivided piece: ONE image fills the whole cell, the same one the board shows - the viewer's own
          // drawing if they drew here, else a teammate's who did, else the coloured piece.
          const author = pickRevealContributor(tileGroups[tileId] || [], user?.userId, tileId);
          const cellX = col * cellW;
          const cellY = row * cellH;
          if (author) {
            const own = user?.userId != null && String(author.userId) === String(user.userId);
            // eslint-disable-next-line no-await-in-loop
            let img = await loadImageForExport(s3PublicUrl(author.tileImageKey), token);
            const fits = aspectFits(img.naturalWidth / img.naturalHeight, cellW / cellH);
            if (own && !fits && fallbackSrc) {
              // The viewer's own drawing is ALWAYS in their picture: fitted whole over the coloured piece.
              // eslint-disable-next-line no-await-in-loop
              const reference = await loadImageForExport(fallbackSrc, token);
              drawImageCover(ctx, reference, cellX, cellY, cellW, cellH);
              drawImageContain(ctx, img, cellX, cellY, cellW, cellH);
              continue;
            }
            if (!own && !fits && fallbackSrc) {
              // a teammate's tile saved before the artwork was re-pieced: wrong shape for this cell
              // eslint-disable-next-line no-await-in-loop
              img = await loadImageForExport(fallbackSrc, token);
            }
            drawImageCover(ctx, img, cellX, cellY, cellW, cellH);
          } else if (fallbackSrc) {
            // eslint-disable-next-line no-await-in-loop
            const reference = await loadImageForExport(fallbackSrc, token);
            drawImageCover(ctx, reference, cellX, cellY, cellW, cellH);
          }
        }

        const mime = format === 'jpg' ? 'image/jpeg' : 'image/png';
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Failed to export the artwork'))),
            mime,
            format === 'jpg' ? 0.92 : undefined
          );
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `collaborative-canvas.${format === 'jpg' ? 'jpg' : 'png'}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        Swal.fire(
          'Download failed',
          `${err?.message || 'Failed to export the artwork.'} Check your internet connection and try again.`,
          'error'
        );
      } finally {
        setDownloading(false);
      }
    },
    [outlineUrls, masterUrls, macroCols, macroRows, boardAspectRatio, tileGroups, downloading, adminToken, sessionId, organizationId, user?.userId]
  );

  useEffect(() => {
    document.body.classList.add('canvas-reveal-active');
    return () => document.body.classList.remove('canvas-reveal-active');
  }, []);

  return (
    <div className="canvas-reveal-page" style={pageStyle}>
      {revealTitle ? (
        <header className="canvas-reveal-header">
          <h1 className="canvas-reveal-title">{revealTitle}</h1>
        </header>
      ) : null}

      <div className="canvas-reveal-live-wrap">
        {outlineUrls.length > 0 ? (
          <>
            <LiveCanvasBoard
              sessionId={sessionId}
              organizationId={organizationId}
              outlineUrls={outlineUrls}
              masterUrls={masterUrls}
              currentUserId={user?.userId ? String(user.userId) : null}
              macroRows={macroRows}
              macroCols={macroCols}
              showStats={false}
              useMasterFallback
              finalView
              ownGame={ownGame}
              ownName={ownName}
            />
            {downloadEnabled ? (
              <div className="canvas-reveal-download-group">
                <button
                  type="button"
                  className="canvas-reveal-download-label"
                  onClick={() => handleDownload('png')}
                  disabled={downloading}
                >
                  {downloading ? 'Downloading…' : 'Download'}
                </button>
                <button
                  type="button"
                  className="canvas-reveal-download-icon-btn"
                  onClick={() => handleDownload('png')}
                  disabled={downloading}
                  aria-label="Download artwork"
                >
                  {downloading ? (
                    <span className="canvas-reveal-download-icon-spinner" aria-hidden />
                  ) : (
                    <svg viewBox="0 0 24 24" aria-hidden width="20" height="20">
                      <path
                        fill="currentColor"
                        d="M12 16 6.5 10.5l1.4-1.4L11 12.2V3h2v9.2l3.1-3.1 1.4 1.4L12 16Zm-7 3v-3h2v3h10v-3h2v3a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2Z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            ) : null}
          </>
        ) : boardState === 'loading' ? (
          <p className="canvas-reveal-empty canvas-reveal-loading" role="status">
            <span className="canvas-reveal-download-icon-spinner canvas-reveal-loading-spinner" aria-hidden />
            Loading the artwork…
          </p>
        ) : (
          <p className="canvas-reveal-empty">Team artwork preview unavailable</p>
        )}
      </div>

      {downloadEnabled && outlineUrls.length > 0 ? (
        <button
          type="button"
          className="canvas-reveal-download-btn-below"
          onClick={() => handleDownload('png')}
          disabled={downloading}
        >
          {downloading ? 'Downloading…' : 'Download Artwork'}
        </button>
      ) : null}
    </div>
  );
}
